/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/**
 * Required to use React components.
 */
import * as React from 'react';

/**
 * Used for the basic page layout.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle';

import { faTimesCircle } from '@fortawesome/pro-duotone-svg-icons/faTimesCircle';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';
import { Alert, AlertTitle, Box, Button, Grid2, ListItem, Paper, styled, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import EConsentContext, { EConsentUserType } from '../contexts/EConsentContext';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IPatientEConsentForms {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledWrapper = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0),

    [theme.breakpoints.up('md')]: {
        maxWidth: 1000,
        margin: 'auto',
        padding: theme.spacing(3),
    }
}))

const StyledHeader = styled(Typography)(({ theme }) => ({
    paddingBottom: theme.spacing(1.5),
    textAlign: 'center',
    fontSize: '1.25rem',
    color: theme.palette.primary.main,

    [theme.breakpoints.down('sm')]: {
        textAlign: 'left',
        width: '80%'
    },
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3, 3, 3, 3),

    [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(5, 2, 5, 2),
        border: 'none',
        boxShadow: 'none'
    }
}))

const InfoBox = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(1.5)
}))

const StyledListItem = styled(ListItem)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: 0,
    padding: '1rem',
    display: 'block'
}))

const ListItemContainer = styled(Box)(({ theme }) => ({
    marginTop: '1.5rem',

    '& > :last-child': {
        borderBottom: `1px solid ${theme.palette.grey[300]} !important`
    }
}))

const GridItem = styled(Grid2)(({ theme }) => ({
    textAlign: 'center',
    paddingLeft: '0.25rem',
    paddingRight: '0.25rem',

    [theme.breakpoints.only('xs')]: {
        paddingTop: '1rem',
        paddingLeft: '0rem',
        paddingRight: '0rem',
        textAlign: 'left',

        '& a':{
            margin: 0
        }
    }
}))

// const useStyles = makeStyles(theme => ({
//     gridItem: {
//         
//     }
// }));

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const PatientEConsentPage: React.FunctionComponent = () => {
    // const { patient } = React.useContext(PatientContext);

    // const [patientValidation, patientValidationLoadState, patientValidationActions] = usePatientValidationByStudyNumber(patient!.studyNumber, true);

    //  const patientValidationContext = React.useMemo(() => {
    //      return {
    //          validation: patientValidation,
    //          loadState: patientValidationLoadState,
    //          actions: patientValidationActions
    //      };
    //  }, [patientValidation, patientValidationLoadState, patientValidationActions]);

    // const notLoading = patientValidationContext.loadState.state !== RequestState.None &&
    //     patientValidationContext.loadState.state !== RequestState.Pending;

    return (
        <>
            <PatientEConsentForms />
        </>
    );
}

const PatientEConsentForms: React.FunctionComponent<IPatientEConsentForms> = () => {
    const { consentEvent, consentForms, userType } = React.useContext(EConsentContext);

    const completed = React.useMemo(() => {
        return (userType === EConsentUserType.Participant && consentEvent?.datePatientCompleted != null) || 
            (userType === EConsentUserType.Investigator && consentEvent?.dateInvestigatorCompleted != null)
    }, [consentEvent, userType]);

    const isParticipant = userType === EConsentUserType.Participant;

    if(!consentEvent) {
        return <>
            <StyledWrapper>
                <StyledPaper elevation={3}>
                    <Alert severity="error" icon={<FontAwesomeIcon fixedWidth icon={faTimesCircle} />} >
                        <AlertTitle>
                            Consent Forms Not Available
                        </AlertTitle>
                        An error occured while loading the consent form. Please report the issue to your treatment team.
                    </Alert>
                </StyledPaper>
            </StyledWrapper>
        </>
    }

    return (
        <>
            <StyledWrapper>
                <StyledPaper elevation={3}>
                    <>
                        <StyledHeader>
                            <strong>Welcome to the BCT 2401 &ndash; PROSPECTIVE e-Consent platform</strong>
                        </StyledHeader>
                        {
                            completed && <>
                                <InfoBox>
                                    <Alert severity="success" icon={<FontAwesomeIcon fixedWidth icon={faCheckCircle} />} >
                                        <AlertTitle>
                                            e-Consent Forms Complete
                                        </AlertTitle>
                                        Thank you for taking the time to complete the e-Consent forms.
                                    </Alert>
                                </InfoBox>
                            </>
                        }
                        {
                            isParticipant && <InfoBox>
                                <Alert severity="info" icon={<FontAwesomeIcon fixedWidth icon={faInfoCircle} />} >
                                    Individual e-Consent form response will not be viewed by a member of the treatment team.
                                    If you have any concerns please report it directly to your treatment team.
                                </Alert>
                            </InfoBox>
                        }
                        <ListItemContainer>
                            {
                                consentForms?.map((form, index) => {
                                    return <StyledListItem key={index}>
                                        <Grid2 container>
                                            <Grid2 
                                                size={{
                                                    xl: 4,
                                                    lg: 4,
                                                    md: 4,
                                                    sm: 6,
                                                    xs: 12
                                                }}
                                                alignContent='center'
                                            >
                                                <Typography>{form.formDefinition?.name}</Typography>
                                            </Grid2>
                                            <GridItem 
                                                size={{
                                                    xl: 4,
                                                    lg: 4,
                                                    md: 4,
                                                    sm: 3,
                                                    xs: 12
                                                }}
                                            >
                                                {
                                                    form?.datePatientSignedConsent ? 
                                                        <Typography
                                                            color="secondary"
                                                        >
                                                            <small>Participant signed on {DateTime.fromISO(form.datePatientSignedConsent).toFormat('dd MMM yyyy')}</small>
                                                        </Typography> : 
                                                        userType === EConsentUserType.Participant ? 
                                                            <Button 
                                                                variant='contained' 
                                                                size='small'
                                                                color='secondary'
                                                                component={Link}
                                                                to={`${form?.formDefinition?.code}`}
                                                            >
                                                                Sign
                                                            </Button> : 
                                                            <Typography>
                                                                <small>Participant not signed</small>
                                                            </Typography>
                                                }
                                                
                                            </GridItem>
                                            <GridItem 
                                                size={{
                                                    xl: 4,
                                                    lg: 4,
                                                    md: 4,
                                                    sm: 3,
                                                    xs: 12
                                                }}
                                            >
                                                {
                                                    form?.dateInvestigatorSignedConsent ? 
                                                        <Typography
                                                            color="secondary"
                                                        >
                                                            <small>Investigator signed on {DateTime.fromISO(form.dateInvestigatorSignedConsent).toFormat('dd MMM yyyy')}</small>
                                                        </Typography> : 
                                                        userType === EConsentUserType.Investigator ? 
                                                            <Button 
                                                                variant='contained' 
                                                                size='small'
                                                                color='secondary'
                                                                component={Link}
                                                                to={`${form?.formDefinition?.code}`}
                                                            >
                                                                Sign
                                                            </Button> : 
                                                            <Typography>
                                                                <small>Investigator not signed</small>
                                                            </Typography>
                                                }
                                            </GridItem>
                                        </Grid2>
                                    </StyledListItem>
                                })
                            }
                        </ListItemContainer>
                    </>
                </StyledPaper>
            </StyledWrapper>
        </>
    );
}

/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default PatientEConsentPage;
